import React, { useCallback, useContext, useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import SearchBar from '../components/SearchBar';
import Layout from '../components/Layout';
import Pagination from '../components/Common/Pagination';
import DateRangePicker from '../components/Attendance/DateRangePicker';

import CustomerCreateModal from '../components/Customer/CustomerCreateModal';
import CustomerEditModal from '../components/Customer/CustomerEditModal';
import CreateQueryModal from '../components/Query/CreateQueryModal';
import CreateFollowUpModal from '../components/Followup/CreateFollowupModal';
import CustomerInfoModal from '../components/Customer/CustomerInfoModal';

import { Customer } from '../types/Customer';
import '../css/Pages.css';
import '../css/PackagesPage.css';
import { fetchCustomers } from '../api/Customer';
import { fetchUserById } from '../api/User';
import { AuthContext } from '../context/AuthContext';
import { formatDate, parseDateValue } from '../utils/date';
import * as FiIcons from 'react-icons/fi';
const FaEye = FiIcons.FiEye as unknown as React.FC;
const FaPlus = FiIcons.FiPlus as unknown as React.FC;
const FiClock = FiIcons.FiClock as unknown as React.FC;
const FiMessageCircle = FiIcons.FiMessageCircle as unknown as React.FC;
const FiEdit2 = FiIcons.FiEdit2 as unknown as React.FC;
const FiCalendar = FiIcons.FiCalendar as unknown as React.FC;

const PAGE_SIZE = 50;

const toDateKey = (value?: string) => {
  const date = parseDateValue(value);
  if (!date || Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatFilterDate = (value: string) => {
  const date = parseDateValue(value);
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : value;
};

const getWhatsAppUrl = (phoneNumber: string, agentPhoneNumber: string) => {
  const digits = (phoneNumber || '').replace(/\D/g, '');
  if (digits.length < 10) return null;

  const whatsappNumber = `91${digits.slice(-10)}`;
  const whatsappMessage = `Hi \u{1F44B}
Thank you for taking the time to speak with us today. It was lovely interacting with you! \u{1F60A}

If you need any assistance with flights, hotels, visas or holiday packages, we\u2019re just a ping away \u2014 happy to help anytime!

${agentPhoneNumber ? `\u{1F4DE} Call Us: ${agentPhoneNumber}\n` : ''}
\u{1F310} Website: https://otaprime.com

Looking forward to planning your next trip \u2708\uFE0F\u2728
Team OTA Prime Holidays`;

  return `https://api.whatsapp.com/send?phone=${whatsappNumber}&text=${encodeURIComponent(whatsappMessage)}`;
};

const CustomersPage = () => {
  const authContext = useContext(AuthContext) as {
    user?: { id?: number | string; phoneNumber?: string; phone?: string } | null;
  };
  const hasFetched = useRef(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerToEdit, setCustomerToEdit] = useState<Customer | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [cityFilter, setCityFilter] = useState('');
  const [areaFilter, setAreaFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [createdStartDateFilter, setCreatedStartDateFilter] = useState('');
  const [createdEndDateFilter, setCreatedEndDateFilter] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showCustomerCreateModal, setShowCustomerCreateModal] = useState(false);
  const [showCreateQueryModal, setShowCreateQueryModal] = useState(false);
  const [showCreateFollowUpModal, setShowCreateFollowUpModal] = useState(false);
  const [customerId, setCustomerId] = useState(0);
  const [queryId, setQueryId] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [serverPaginated, setServerPaginated] = useState(false);
  const [serverTotalPages, setServerTotalPages] = useState(1);
  const [agentPhoneNumber, setAgentPhoneNumber] = useState(
    authContext.user?.phoneNumber || authContext.user?.phone || '',
  );
  const lastLoadedPageRef = useRef<number | null>(null);
  // Track which customer phone number is revealed at a time.
  const [revealedKey, setRevealedKey] = useState<string | null>(null);

  const maskPhone = (phone?: string) => {
    const s = String(phone || '').replace(/\s+/g, '');
    if (!s) return '-';
    const keep = 2;
    const endKeep = 2;
    const len = s.length;
    if (len <= keep + endKeep) return '*'.repeat(Math.max(3, len));
    const maskedLen = Math.max(3, len - keep - endKeep);
    return `${s.slice(0, keep)}${'*'.repeat(maskedLen)}${s.slice(-endKeep)}`;
  };

  const load = useCallback(async (targetPage: number) => {
    try {
      setLoading(true);
      const data = await fetchCustomers({ page: targetPage, limit: PAGE_SIZE });
      lastLoadedPageRef.current = targetPage;

      if (Array.isArray(data)) {
        setCustomers(data);
        setServerPaginated(false);
        setServerTotalPages(Math.max(1, Math.ceil(data.length / PAGE_SIZE)));
        return;
      }

      const list = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.customers)
          ? data.customers
          : Array.isArray(data?.items)
            ? data.items
            : [];
      const pagination = data?.pagination || {};
      const total = Number(data?.total ?? pagination.total ?? pagination.count ?? list.length);
      const totalPages = Number(
        data?.totalPages ?? pagination.totalPages ?? pagination.pages ?? Math.max(1, Math.ceil(total / PAGE_SIZE))
      );
      setCustomers(prev => {
        if (targetPage <= 1) return list;
        const existingIds = new Set(prev.map((c: Customer) => c.id).filter(Boolean));
        const deduped = list.filter((c: Customer) => !c.id || !existingIds.has(c.id));
        return [...prev, ...deduped];
      });
      setServerPaginated(true);
      setServerTotalPages(totalPages);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    load(1);
  }, [load]);

  useEffect(() => {
    const phoneFromToken = authContext.user?.phoneNumber || authContext.user?.phone || '';
    setAgentPhoneNumber(phoneFromToken);

    if (phoneFromToken || !authContext.user?.id) return;

    fetchUserById(String(authContext.user.id))
      .then((user) => setAgentPhoneNumber(user.phoneNumber || ''))
      .catch(() => setAgentPhoneNumber(''));
  }, [authContext.user]);

  useEffect(() => {
    if (!hasFetched.current || !serverPaginated) return;
    if (lastLoadedPageRef.current === page) return;
    load(page);
  }, [page, serverPaginated, load]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, cityFilter, areaFilter, sourceFilter, createdStartDateFilter, createdEndDateFilter]);

  const closeAllModals = () => {
    setShowCustomerCreateModal(false);
    setShowCreateQueryModal(false);
    setShowCreateFollowUpModal(false);
    setCustomerToEdit(null);
  };

  const handleAddQuery = (customerId?: number) => {
    setCustomerId(customerId || 0);
    setShowCreateQueryModal(true);
  };

  const navigate = useNavigate();
  const handleViewQueries = (customerId?: number) => {
    navigate(`/queries?customerId=${customerId}`);
  };

  const filterOptions = useMemo(() => {
    const valuesFor = (getValue: (customer: Customer) => string | undefined) => Array.from(
      new Set(customers.map(getValue).filter((value): value is string => Boolean(value?.trim())))
    ).sort((first, second) => first.localeCompare(second));

    return {
      cities: valuesFor(customer => customer.city),
      areas: valuesFor(customer => customer.area),
      sources: valuesFor(customer => customer.source),
    };
  }, [customers]);

  const filteredCustomers = useMemo(() => {
    const matchesFilter = (value: string | undefined, filter: string) => (
      !filter || value?.trim().toLowerCase() === filter.toLowerCase()
    );
    const normalizedSearch = searchTerm.toLowerCase();

    return customers.filter(customer => {
      const matchesSearch = (customer.name || '').toLowerCase().includes(normalizedSearch) ||
        (customer.phoneNumber || '').toString().toLowerCase().includes(normalizedSearch) ||
        (customer.city || '').toLowerCase().includes(normalizedSearch);
      const createdDate = toDateKey(customer.createdAt || customer.created_at || '');
      const matchesCreatedDate = !createdStartDateFilter || !createdEndDateFilter || (
        Boolean(createdDate) && createdDate >= createdStartDateFilter && createdDate <= createdEndDateFilter
      );

      return matchesSearch &&
        matchesFilter(customer.city, cityFilter) &&
        matchesFilter(customer.area, areaFilter) &&
        matchesFilter(customer.source, sourceFilter) &&
        matchesCreatedDate;
    });
  }, [customers, searchTerm, cityFilter, areaFilter, sourceFilter, createdStartDateFilter, createdEndDateFilter]);

  const totalPages = serverPaginated
    ? Math.max(1, serverTotalPages)
    : Math.max(1, Math.ceil(filteredCustomers.length / PAGE_SIZE));
  const pagedCustomers = useMemo(() => {
    if (serverPaginated) return filteredCustomers;
    return filteredCustomers.slice(0, page * PAGE_SIZE);
  }, [filteredCustomers, page, serverPaginated]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  useEffect(() => {
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const nearBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 200;
        if (nearBottom && !loading && page < totalPages) {
          setPage(prev => Math.min(totalPages, prev + 1));
        }
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, [loading, page, totalPages]);

  return (
    <Layout>
      <div className="crm-page packages-page">
        <div className="packages-header">
          <div>
            <h2>Customers</h2>
            <p className="page-hint">Manage CRM customer records, contact details, and follow-up entry points.</p>
          </div>
          <div className="packages-actions">
            <button className="btn" onClick={() => setShowCustomerCreateModal(true)} title="Create a new customer">
              <FaPlus /> Add Customer
            </button>
          </div>
        </div>

        <div className="packages-filters customers-filters">
          <div className="customers-filter-row customers-filter-row--primary">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              placeholder="Search by name, phone, or city"
            />
            <button
              type="button"
              className="btn-secondary customers-date-filter"
              onClick={() => setShowDatePicker(true)}
              aria-label="Filter by customer creation date"
            >
              <FiCalendar />
              {createdStartDateFilter && createdEndDateFilter
                ? createdStartDateFilter === createdEndDateFilter
                  ? formatFilterDate(createdStartDateFilter)
                  : `${formatFilterDate(createdStartDateFilter)} – ${formatFilterDate(createdEndDateFilter)}`
                : 'Created dates'}
            </button>
          </div>
          <div className="customers-filter-row customers-filter-row--secondary">
            <select className="filter-select" value={cityFilter} onChange={event => setCityFilter(event.target.value)} aria-label="Filter by city">
              <option value="">All cities</option>
              {filterOptions.cities.map(city => <option key={city} value={city}>{city}</option>)}
            </select>
            <select className="filter-select" value={areaFilter} onChange={event => setAreaFilter(event.target.value)} aria-label="Filter by area">
              <option value="">All areas</option>
              {filterOptions.areas.map(area => <option key={area} value={area}>{area}</option>)}
            </select>
            <select className="filter-select" value={sourceFilter} onChange={event => setSourceFilter(event.target.value)} aria-label="Filter by source">
              <option value="">All sources</option>
              {filterOptions.sources.map(source => <option key={source} value={source}>{source}</option>)}
            </select>
          </div>
          <div className="customers-filter-row customers-filter-row--actions">
            <button
              className="clear-btn"
              disabled={!cityFilter && !areaFilter && !sourceFilter && !createdStartDateFilter && !createdEndDateFilter}
              onClick={() => {
                setCityFilter('');
                setAreaFilter('');
                setSourceFilter('');
                setCreatedStartDateFilter('');
                setCreatedEndDateFilter('');
              }}
            >
              Clear filters
            </button>
          </div>
        </div>

        {showDatePicker && (
          <DateRangePicker
            startDate={createdStartDateFilter}
            endDate={createdEndDateFilter}
            onDateChange={(startDate, endDate) => {
              setCreatedStartDateFilter(startDate);
              setCreatedEndDateFilter(endDate);
            }}
            onClose={() => setShowDatePicker(false)}
          />
        )}

        <div className="packages-table-wrapper">
          <table className="packages-table customers-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Phone Number</th>
                <th>Source</th>
                <th>City</th>
                <th>Area</th>
                <th>Created At</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedCustomers
                .map((c, idx) => {
                  const whatsappUrl = getWhatsAppUrl(c.phoneNumber, agentPhoneNumber);

                  return (
                    <tr key={c.id ?? idx}>
                      <td>
                        <span
                          className="clickable-name"
                          onClick={() => setSelectedCustomer(c)}
                        >
                          {c.name}
                        </span>
                      </td>
                      <td>
                        {(() => {
                          const key = `phone:${c.id ?? 'idx-'+idx}`;
                          const isShown = revealedKey === key;
                          return isShown ? (
                            <a href={`tel:${c.phoneNumber}`}>{c.phoneNumber}</a>
                          ) : (
                            <span className="masked" onClick={() => setRevealedKey(key)} title="Click to reveal phone">
                              {maskPhone(c.phoneNumber)}
                            </span>
                          );
                        })()}
                      </td>
                      <td>
                        {c.source ? <span className="customer-source-badge">{c.source}</span> : '-'}
                      </td>
                      <td>{c.city || '-'}</td>
                      <td>{c.area || '-'}</td>
                      <td>{formatDate(c.createdAt || c.created_at) || '-'}</td>
                      <td className="col-actions">
                        <div className="action-buttons customer-action-buttons">
                          <button className="btn-secondary icon-only" title="View Queries" aria-label="View Queries" onClick={() => handleViewQueries(c.id)}>
                            <FaEye />
                          </button>
                          <button className="btn-secondary icon-only" title="Add Query" aria-label="Add Query" onClick={() => handleAddQuery(c.id)}>
                            <FaPlus />
                          </button>
                          <button className="btn-secondary icon-only" title="Edit Customer" aria-label="Edit Customer" onClick={() => setCustomerToEdit(c)}>
                            <FiEdit2 />
                          </button>
                          <button className="btn-secondary icon-only" title="View Follow-ups" aria-label="View Follow-ups" onClick={() => navigate(`/followups?customerId=${c.id}`)}>
                            <FiClock />
                          </button>
                          {whatsappUrl ? (
                            <a
                              className="btn-secondary icon-only"
                              title="Send WhatsApp message"
                              aria-label="Send WhatsApp message"
                              href={whatsappUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <FiMessageCircle />
                            </a>
                          ) : (
                            <button className="btn-secondary icon-only" title="No valid phone number" aria-label="WhatsApp unavailable: no valid phone number" disabled>
                              <FiMessageCircle />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              {filteredCustomers.length === 0 && (
                <tr>
                  <td colSpan={7} className="empty-state">No customers found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      </div>

      {selectedCustomer && (
        <CustomerInfoModal
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
        />
      )}

      {customerToEdit && (
        <CustomerEditModal
          customer={customerToEdit}
          onClose={() => setCustomerToEdit(null)}
          onCustomerUpdated={(updatedCustomer) => {
            setCustomers(current => current.map(customer => (
              customer.id === updatedCustomer.id ? updatedCustomer : customer
            )));
            setSelectedCustomer(current => (
              current?.id === updatedCustomer.id ? updatedCustomer : current
            ));
            setCustomerToEdit(null);
          }}
        />
      )}

      {showCustomerCreateModal && (
        <CustomerCreateModal
          onClose={closeAllModals}
          onCustomerCreated={async (id) => {
            setCustomerId(id);
            setShowCustomerCreateModal(false);
            setPage(1);
            await load(1);
            setShowCreateQueryModal(true);
          }}
        />
      )}

      {showCreateQueryModal && (
        <CreateQueryModal
          customerId={customerId}
          onClose={closeAllModals}
          onQueryCreated={(id) => {
            setQueryId(id);
            setShowCreateQueryModal(false);
            setShowCreateFollowUpModal(true);
          }}
        />
      )}

      {showCreateFollowUpModal && (
        <CreateFollowUpModal
          queryId={queryId}
          required
          onClose={closeAllModals}
        />
      )}
    </Layout>
  );
};

export default CustomersPage;
